'use client';

import { hasRuntimeObjectType, isStringValue } from '../lib/validation/primitives';


import { useCallback } from 'react';
import { useTranslations } from 'next-intl';

import { APP_COPY } from '@/lib/app-copy';

import { makeFallbackAccessor, type TranslationValues } from './fallback';

/**
 * App-shell string accessor for Client Components.
 *
 * The dashboard is overwhelmingly client-side, so this is the main entry point
 * for phase 1r. It reads NextIntlClientProvider, which `dashboard/layout.tsx`
 * supplies with the full catalog for the user's `users.locale`.
 *
 * Two ways to call it:
 *
 *   const t = useAppCopy();
 *   t('shell.openMenu')                  // key relative to the `app.` namespace
 *   t('nav.settings', 'Settings')        // explicit fallback
 *
 * With no explicit fallback it resolves one from APP_COPY itself, so the English
 * source is never duplicated at the call site and cannot drift from it.
 *
 * Interpolated strings pass ICU values as the LAST argument, which means the
 * fallback has to be spelled out because the two-argument form is ambiguous:
 *
 *   t('toast.itemsAdded', undefined, { count: n })
 *   t('toast.planCanceledUntil', undefined, { date: periodEndLabel })
 *
 * `undefined` still resolves the English source from APP_COPY — it only opts
 * out of OVERRIDING it.
 */
export function useAppCopy() {
  const translate = useTranslations();

  // Stable across renders: callers pass this into useMemo dependency lists (the
  // transcript tabs do), and a fresh closure each render would rebuild those
  // memos on every keystroke.
  return useCallback(
    (key: string, fallback?: string, values?: TranslationValues): string => {
      const get = makeFallbackAccessor(translate);

      return get(`app.${key}`, fallback ?? sourceString(key) ?? key, values);
    },
    [translate]
  );
}

/** Walks APP_COPY for the English source behind a dotted key. */
function sourceString(key: string): string | undefined {
  let node: unknown = APP_COPY;

  for (const segment of key.split('.')) {
    if (!hasRuntimeObjectType(node) || node === null) return undefined;
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    node = (node as Record<string, unknown>)[segment];
  }

  return isStringValue(node) ? node : undefined;
}

/**
 * Resolves a `messageKey` carried by a data module (sidebar nav items,
 * transcript tabs) against the catalog.
 *
 * Those modules keep their English label as the source of truth and add a key
 * pointing at its catalog entry — the same convention the marketing nav and
 * footer use. An item with no `messageKey` is deliberately untranslated and
 * renders its English label unchanged.
 */
export function useLabelResolver() {
  const translate = useTranslations();

  return useCallback(
    (label: string, messageKey?: string): string => {
      if (!messageKey) return label;

      return makeFallbackAccessor(translate)(messageKey, label);
    },
    [translate]
  );
}
